class BundleLevel extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;
    this.parentBuilder = this.closest('bundle-builder');
    this.initializeLevel();
  }

  initializeLevel() {
    this.isGift = this.hasAttribute('is-gift')
    this.isOptional = this.hasAttribute('is-optional')
    this.detailsEl = this.querySelector('details.accordion__disclosure');
    this.levelTitle = this.getAttribute('data-title');
    this.levelIndex = this.getAttribute('data-index');
    this.minValue = Number(this.getAttribute('data-min') || 0);
    this.maxValue = Number(this.getAttribute('data-max') || 0);
    this.totalCount = 0;
    this.progressEl = this.querySelector('.bb-level__progress');
    this.cards = Array.from(this.querySelectorAll('.bb-card'));
    this.cards.forEach(card => this.setupCard(card));
    this.checkEmptyLevel();

    if (this.isGift) {
      if (this.progressEl) this.progressEl.hidden = true;
      this.updateProgress();
      requestAnimationFrame(() => this.emitLevelChanged());
      return;
    }

    if(this.isOptional){
      this.totalCount = this.cards.reduce((s, c) => s + (c.quantity || 0), 0);
      this.updatePlusButtonsState();
    }

    requestAnimationFrame(() => this.emitLevelChanged())
    this.updateProgress();
  }

  setupCard(card) {
    const plusBtn = card.querySelector('.bb-qty--plus');
    const minusBtn = card.querySelector('.bb-qty--minus');
    const countEl = card.querySelector('.bb-qty__count');
    card.inventory = Number(card.dataset.variantStock || 0);
    card.inventoryPolicy = card.dataset.inventoryPolicy || 'deny';

    if (card.inventory <= 0 && card.inventoryPolicy !== 'continue') {
      card.style.display = "none";
      return;
    }

    if (this.isGift) {
      card.quantity = 1;
      if (countEl) {
        countEl.textContent = '1';
        countEl.hidden = false;
      }
      if (plusBtn) plusBtn.style.display = 'none';
      if (minusBtn) minusBtn.style.display = 'none';

      this.totalCount += 0;
      this.emitLevelChanged();
      return;
    }
    card.quantity = Number(countEl?.textContent || 0) || 0;

    const renderCard = () => {
      if(countEl){
        countEl.textContent = card.quantity;
        countEl.hidden = card.quantity === 0;
      }
      if(minusBtn) {
        minusBtn.disabled = card.quantity <= 0;
      }
    };

    const increment = () => {
      if (this.maxValue && this.totalCount >= this.maxValue) return;
      const canContinueSelling = card.inventoryPolicy === 'continue';
      if (!canContinueSelling && card.inventory != null && card.quantity >= card.inventory) return;

      card.quantity++;
      this.totalCount++;
      postChange();
      this.openNextIncompleteLevel();
    };

    const decrement = () => {
      if (card.quantity <= 0) return;
      card.quantity--;
      this.totalCount--;
      postChange();
    };

    const postChange = () => {
      renderCard();
      this.updateProgress();
      this.updatePlusButtonsState();
      this.emitLevelChanged()
    };

    plusBtn?.addEventListener('click', increment);
    minusBtn?.addEventListener('click', decrement);

    renderCard();
  }

  checkEmptyLevel() {
    const available = this.cards.filter(c =>
      c.inventoryPolicy === 'continue' || Number(c.inventory || c.dataset.variantStock || 0) > 0
    );
    this.hasVariants  = available.length > 0;
    if (!this.hasVariants) this.style.display = "none";
  }


  updateProgress() {
    if (!this.isGift && this.progressEl) {
      const visibleTotal = Math.max(this.minValue || 0, this.totalCount || 0);
      this.progressEl.textContent = `(${this.totalCount}/${visibleTotal})`;
    }
    this.setAttribute('data-count', this.totalCount);
  }

  updatePlusButtonsState() {
    const disableAllPlus = this.maxValue > 0 && this.totalCount >= this.maxValue;
    this.cards.forEach(card => {
      const plusButton = card.querySelector('.bb-qty--plus');
      if (plusButton) {
        const currentVarInventory = card.inventory != null ? card.inventory : (Number(card.dataset.variantStock) || null);
        const currentVarInventoryPolicy = card.inventoryPolicy ?? card.dataset.inventoryPolicy ?? 'deny';
        const cardQty = card.quantity || 0;
        const canContinueSelling = currentVarInventoryPolicy === 'continue';
        const variantLimitReached = !canContinueSelling && currentVarInventory != null && cardQty >= currentVarInventory;


        plusButton.disabled = disableAllPlus || variantLimitReached;
      }
    });
  }

  emitLevelChanged() {
    const items = this.cards.map(card => {
      const qty = card.quantity;
      if (qty > 0) return {
        id: card.dataset.variantId,
        title: card.querySelector('.bb-card__title')?.textContent || '',
        img: card.querySelector('.bb-card__img')?.currentSrc || card.querySelector('.bb-card__img')?.src,
        quantity: qty,
        unitPrice: Number(card.dataset.variantPrice ) || 0,
        total: qty * (Number(card.dataset.variantPrice) || 0)
      };
      return null;
    }).filter(Boolean);

    const totalPrice = items.reduce((sum, item) => sum + item.total, 0);

    this.dispatchEvent(new CustomEvent('bb:level-changed', {
      bubbles: true,
      composed: true,
      detail: {
        isGift: this.isGift,
        isOptional: this.isOptional,
        index: this.levelIndex,
        id: this.detailsEl?.id || `bundle-level-${this.levelIndex}`,
        title: this.levelTitle,
        min: this.minValue,
        max: this.maxValue,
        count: this.totalCount,
        valid: this.isGift ? true : this.totalCount >= this.minValue,
        full: this.isGift ? true : this.totalCount >= this.maxValue,
        remainingToMin: this.isGift ? 0 : Math.max(0, this.minValue - this.totalCount),
        items,
        totalPrice
      }
    }));
  }

  openNextIncompleteLevel() {
    if (!(this.maxValue > 0 && this.totalCount >= this.maxValue)) return;
    const builder = this.parentBuilder;
    if (!builder) return;
    const levels = Array.from(builder.querySelectorAll('bundle-level'));
    const next = levels.find(level =>
      level !== this &&
      level.totalCount < level.maxValue
    );
    const optional = levels.find(l => l.hasAttribute('is-optional'));
    const gift = levels.find(l => l.hasAttribute('is-gift'));
    let optionalWasShown = false;
    let giftWasShown = false;

    if (next) {
     this.focusLevel(next);
    }else if(optional && !optionalWasShown){
      optionalWasShown = true;
      this.focusLevel(optional);
    }
    else if(gift && !giftWasShown){
      giftWasShown = true;
      this.focusLevel(gift);
    }
  }

  focusLevel(levelEl){
    const nextLvlDetails = levelEl.querySelector('details.accordion__disclosure');

    if (nextLvlDetails) {
      nextLvlDetails.open = true;
      nextLvlDetails.setAttribute('aria-expanded', true);
      const nextAccordionContent = nextLvlDetails.querySelector('.accordion__content')
      if (nextAccordionContent) {
        nextAccordionContent.style.opacity = '1'
      }
      nextLvlDetails.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
  resetLevel() {
    if (this.isGift) {
      this.totalCount = 0;
      this.cards.forEach(c => c.quantity = 1);
      this.emitLevelChanged();
      return;
    }

    this.totalCount = 0;
    this.cards.forEach(card => {
      card.quantity = 0;
      const countEl = card.querySelector('.bb-qty__count');
      const minusBtn = card.querySelector('.bb-qty--minus');
      if (countEl) {
        countEl.textContent = '0';
        countEl.hidden = true;
      }
      if (minusBtn) minusBtn.disabled = true;
    });
    this.updateProgress();
    this.updatePlusButtonsState();
    this.emitLevelChanged();
  }

}

class BundleBuilder extends HTMLElement {
  connectedCallback() {
    this.levelsData = {};
    this.mainProductId = this.getAttribute('data-main-product');

    this._onLevelChanged = this._onLevelChanged.bind(this);
    this._onBundleSubmit = this._onBundleSubmit.bind(this);

    this.addEventListener('bb:level-changed', this._onLevelChanged);
    document.addEventListener('bb:submit', this._onBundleSubmit);
  }

  disconnectedCallback() {
    this.removeEventListener('bb:level-changed', this._onLevelChanged);
    document.removeEventListener('bb:submit', this._onBundleSubmit);
  }

  getTotals() {
    const levels = Object.values(this.levelsData);
    const totalPrice = levels.reduce((sum, lvl) => sum + (lvl.totalPrice || 0), 0);
    const totalItems = levels.reduce((sum, lvl) => sum + (lvl.count || 0), 0);
    const allLevelMinsMet = levels.every(lvl => (lvl.count || 0) >= (lvl.min || 0));

    return {
      totalPrice,
      totalItems,
      allLevelMinsMet
    };
  }

  _onLevelChanged(e) {
    const detail = e.detail;
    this.levelsData[detail.index] = detail;
    const isGift = detail.isGift;

    const {totalPrice, totalItems, allLevelMinsMet } = this.getTotals();
    const sticky = document.querySelector('bundle-sticky');
    if (sticky) {
      const discountPercent = Number(sticky.dataset.discount || 0);
      const minRequired = Number(sticky.dataset.minRequired || 0);

      let discount = 0;
      let discountedTotal = totalPrice;

      if (discountPercent && minRequired && totalItems >= minRequired) {
        discountedTotal = 0;
        discount = 0;

        Object.values(this.levelsData).forEach(lvl => {
          (lvl.items || []).forEach(item => {
            const qty = Number(item.quantity || 0);
            const unitPrice = Number(item.unitPrice || 0) / 100;
              const discountedUnitPrice = Math.round(unitPrice * (1 - discountPercent / 100));
              const itemDiscountedTotal = discountedUnitPrice * qty;
              discountedTotal += itemDiscountedTotal;
          });
        });
        discountedTotal = (discountedTotal * 100);
        discount = totalPrice - discountedTotal;
      }

      sticky.updateTotals({
        totalPrice,
        totalItems,
        discount,
        discountedTotal,
        allLevelMinsMet
      });

      sticky.updateLevel(detail);
    }
  }

  setComponentsAttribute(){
    const components = [];
    Object.values(this.levelsData).forEach(lvl => {
      (lvl.items || []).forEach(it => {
        const qty = parseInt(it.quantity || 0, 10) || 0;
        if (qty > 0) components.push({ id: 'gid://shopify/ProductVariant/' + it.id, quantity: qty });
      });
    });
    return components;
  }

  async _onBundleSubmit() {
  try {
    const masterId = Number(this.mainProductId);
    if (!masterId) return;

    const components = this.setComponentsAttribute();
    if (!components.length) return;

    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      })
    );
    const addRes = await fetch(`${Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{
          id: masterId,
          quantity: 1,
          properties: { _components: JSON.stringify(components) }
        }],
        sections: sectionsToBundle.join(','),
        sections_url: window.location.pathname
      })
    });

    const updateRes = await fetch(`${Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        updates: {},
        sections: sectionsToBundle.join(','),
        sections_url: window.location.pathname
      })
    });
    const fullCart = await updateRes.json();

    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: {
        baseEvent: 'variant:add',
        onSuccessDo: 'force_open_drawer',
        cart: fullCart
      }
    }));

  } catch (err) {
    console.error('Error adding bundle:', err);
  } finally {
    this.resetBundle?.();
  }
}



  resetBundle() {
    this.levelsData = {};

    const levels = this.querySelectorAll('bundle-level');
    levels.forEach(level => {
      if (typeof level.resetLevel === 'function') {
        level.resetLevel();
      } else {
        level.totalCount = 0;
        level.updateProgress?.();
      }
    });

    const sticky = document.querySelector('bundle-sticky');
    if (sticky) {
      sticky.closeSticky?.();
    }
  }

}

class BundleSticky extends HTMLElement {
  constructor() {
    super();
    this._bundleStickyEnd = null;

  }

  connectedCallback(){

    const builder = this.closest('bundle-builder') || document;
    const levels  = builder.querySelectorAll('bundle-level');

    this.maxTotal = Array.from(levels).reduce((sum, el) => {
      const max = parseInt(el.getAttribute('data-max') || '0', 10) || 0;
      return sum + max;
    }, 0);

    this.minTotal = Array.from(levels).reduce((sum, el) => {
      const min = parseInt(el.getAttribute('data-min') || '0', 10) || 0;
      return sum + min;
    }, 0);

    this.progressCountEl = this.querySelector('.bb-progress-count');
    this.atcWrapper = this.querySelector('.bundle-sticky__atc')
    this.atcButton = this.atcWrapper.querySelector('button');
    this.expandButton = this.querySelector('.sticky-expand-toggle');
    this.headSticky = this.querySelector('.bundle-sticky__head');

    this.updateProgress();

    this.emitSubmitAtc = this.emitSubmitAtc.bind(this);
    this.atcButton?.addEventListener('click', this.emitSubmitAtc);

    this.toggleExpand = this.toggleExpand.bind(this);
    this.headSticky?.addEventListener('click', this.toggleExpand);

    this.handleSwatchClick = this.handleSwatchClick.bind(this);
    this.addEventListener('click', this.handleSwatchClick);

    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    document.addEventListener('click', this.handleOutsideClick);

  }

  disconnectedCallback(){
    this.atcButton?.removeEventListener('click', this.emitSubmitAtc);
    this.expandButton?.removeEventListener('click', this.toggleExpand);
    this.removeEventListener('click', this.handleSwatchClick);
    document.removeEventListener('click', this.handleOutsideClick);
  }
  getRootMargin = () => {
    const width = window.innerWidth;

    if (width <= 390) return '0px 0px -45% 0px';
    if (width <= 768) return '0px 0px -48% 0px';
    if (width <= 999) return '0px 0px -35% 0px';
    return '0px 0px -15% 0px';
  };
  updateTotals({ totalPrice, totalItems, discount, discountedTotal, allLevelMinsMet }) {
    const newPriceEl = this.querySelector('.bundle-sticky__new');
    const oldPriceEl = this.querySelector('.bundle-sticky__old');
    const badgeEl = this.querySelector('.bundle-sticky__badge');

    if (newPriceEl) newPriceEl.textContent = formatCents(discountedTotal);
    if (badgeEl) {
      badgeEl.hidden = discount <= 0;
      badgeEl.textContent = discount > 0 ? `${window.localeStrings.savings} ${formatCents(discount)}` : '';
    }
    if (oldPriceEl){
      const isHidden = discount <= 0;
      oldPriceEl.textContent = formatCents(totalPrice)
      oldPriceEl.hidden = discount <= 0;
      oldPriceEl.hidden = isHidden;
      if (newPriceEl){
        newPriceEl.classList.toggle('text-on-sale', !isHidden);
      }
    }
    this.updateProgress(totalItems);
    this._eligibility = {
      allLevelMinsMet: !!allLevelMinsMet,
      allItemsMet: totalItems >= this.minTotal
    };
    this.checkATC();
  }

  updateLevel(lvlData){
    if (lvlData.isGift) return;
    const levels = this.querySelectorAll(`.bundle-sticky__level[data-level-index="${lvlData.index}"]`);
    levels.forEach(levelEl => {
      this.renderRow(levelEl, lvlData)

      if(lvlData.isOptional && lvlData.count === 0 && lvlData.min === 0){
        levelEl.style.display = 'none';
      }else{
        levelEl.style.display = '';
      }
    });
  }

  renderRow(levelEl, lvl) {
    const swatchContainer = levelEl.querySelector('.bundle-sticky__swatches');
    if (!swatchContainer) return;

    swatchContainer.innerHTML = '';

    const { items, min, max, count } = lvl;
    const visibleSlots = Math.max(min, count);

    let filledCount = 0;
    items.forEach(item => {
      for (let i = 0; i < item.quantity; i++) {
        if (filledCount >= visibleSlots) return;
        const imgWrapper = document.createElement('span');
        const imgEl = document.createElement('img');
        imgWrapper.className = 'bundle-sticky__slot filled';
        imgEl.src = item.img;
        imgEl.alt = item.title;
        imgWrapper.appendChild(imgEl);
        swatchContainer.appendChild(imgWrapper);
        filledCount++;
      }
    });

    const remaining = Math.max(0, visibleSlots - filledCount);
    for (let i = 0; i < remaining; i++) {
      const emptySlot = document.createElement('span');
      emptySlot.className = 'bundle-sticky__slot is-empty';
      swatchContainer.appendChild(emptySlot);
    }
  }

  checkATC(){
    const { allLevelMinsMet, allItemsMet } = this._eligibility || {}
    let shouldDisable = !allLevelMinsMet || !allItemsMet;

    const builder = document.querySelector('bundle-builder') || document;
    const xlRequiredTotal = Number(builder.dataset.requiredTotalXl || 0);

    let requiredTitles = [];

    if (xlRequiredTotal && xlRequiredTotal > 0) {

      try {
        requiredTitles = JSON.parse(builder.dataset.requiredLevelsXl || '[]')
          .map(t => t.toLowerCase());
      } catch (e) {
        console.log('Invalid requiredLevelsXL metafield data', e);
      }

      const levels = Object.values(builder.levelsData || {});

      const totalRequiredLvlsSelected = levels
      .filter((lvl) =>
        requiredTitles.includes((lvl.title || '').toLowerCase().trim())
      )
      .reduce((sum, lvl) => sum + (Number(lvl.count) || 0), 0);



      const basicRequiredLevels = levels.filter((lvl) => lvl.isOptional);
      const basicsOK = basicRequiredLevels.length === 0 || basicRequiredLevels.every((lvl) =>
        lvl.count >= (lvl.min || 0));

      if (totalRequiredLvlsSelected >= xlRequiredTotal && basicsOK) {
        shouldDisable = false;
      }else{
        shouldDisable = true;
      }
    }

    this.atcButton.disabled = shouldDisable
  }

  emitSubmitAtc(e){
    e.preventDefault();

    if (this.atcButton.matches(':disabled,[aria-disabled="true"]')) return;
    this.dispatchEvent(new CustomEvent('bb:submit', {
      bubbles: true, composed: true, detail: {}
    }));
  }


  openSticky() {
    if (!this.classList.contains('bundle-sticky--opened')) {
      this.classList.add('bundle-sticky--opened');
      this.querySelector('safe-sticky')?.classList.add('bundle-sticky--opened');
      this.expandButton?.setAttribute('aria-expanded', 'true');
    }
  }

  closeSticky() {
    if (this.classList.contains('bundle-sticky--opened')) {
      this.classList.remove('bundle-sticky--opened');
      this.querySelector('safe-sticky')?.classList.remove('bundle-sticky--opened');
      this.expandButton?.setAttribute('aria-expanded', 'false');
    }
  }

  toggleExpand() {
    if (this.classList.contains('bundle-sticky--opened')) {
      this.closeSticky();
    } else {
      this.openSticky();
    }
  }
  updateProgress(current = 0){
     const visualTotal = Math.max(this.minTotal || 0, current || 0);
     if (this.progressCountEl) this.progressCountEl.textContent = `(${current}/${visualTotal})`;
  }

  handleSwatchClick(e) {
    const swatch = e.target.closest('.bundle-sticky__slot');
    if (!swatch) return;

    const levelEl = swatch.closest('.bundle-sticky__level');
    if (!levelEl) return;

    const levelIndex = levelEl.dataset.levelIndex;
    if (levelIndex == null) return;

    const builder = this.closest('bundle-builder') || document;
    const targetLevel = builder.querySelector(`bundle-level[data-index="${levelIndex}"]`);
    if (!targetLevel) return;

    const details = targetLevel.querySelector('details.accordion__disclosure');
    if (details) {
      details.open = true;
      details.setAttribute('aria-expanded', true);
    }

    targetLevel.scrollIntoView({ behavior: 'smooth', block: 'start' });
    this.closeSticky();
  }

  handleOutsideClick(event) {
    const modal = this;
    const clickedInside = modal.contains(event.target);
    if (!clickedInside && this.classList.contains('bundle-sticky--opened')) {
      this.closeSticky();
    }
  }

}

customElements.define('bundle-level', BundleLevel);
customElements.define('bundle-builder', BundleBuilder);
customElements.define('bundle-sticky', BundleSticky);

//Helpers to format money
function getMoneyFormatter() {
  try {
    return new Intl.NumberFormat(Shopify.locale, { style: 'currency', currency: Shopify.currency.active });
  } catch {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
  }
}

function formatCents(cents) {
  const n = Number.isFinite(cents) ? cents : 0;
  return getMoneyFormatter().format(n / 100);
}
